import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ReferralData, DeduplicationMatch } from '../types';
import PatientInfoGrid from './PatientInfoGrid';
import DeduplicationTable from './DeduplicationTable';
// import CommentsSection from './CommentsSection';
import AuditChangesSection from './AuditChangesSection';
import CaregiverSection from './CaregiverSection';
// import TrackingParametersSection from './TrackingParametersSection';
import CustomAccordion from '../ui/CustomAccordion';
import { ExpandedFieldConfig } from '../Table';
import { STATUS } from '../../LeadManagement/constants';
import { useGetCCPatientsByIdsQuery } from '../../../features/lma/ccPatientApi';

interface ExpandedRowContentProps {
  row: ReferralData;
  expandedSection: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null;
  setExpandedSection: (
    section: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  ) => void;
  newComment: string;
  setNewComment: (comment: string) => void;
  onOpenMergeModal?: (match: DeduplicationMatch | undefined, row: ReferralData) => void;
  onSendToCC?: () => void;
  isSending?: boolean;
  selectedDedupeRecord: string;
  setSelectedDedupeRecord: (id: string) => void;
  hideDedupeSelectColumn?: boolean;
  isProcessedTab?: boolean;
  expandedConfig?: ExpandedFieldConfig;
  statusValue?: string;
}

const ExpandedRowContent: React.FC<ExpandedRowContentProps> = ({
  row,
  expandedSection,
  setExpandedSection,
  // newComment,
  // setNewComment,
  onOpenMergeModal,
  onSendToCC,
  isSending = false,
  selectedDedupeRecord,
  setSelectedDedupeRecord,
  hideDedupeSelectColumn = false,
  isProcessedTab = false,
  expandedConfig,
  statusValue,
}) => {
  // Extract all CC Patient IDs from deduplication matches
  const ccPatientIds = useMemo(() => {
    return (row.deduplication || [])
      .map((match) => match.ccPatientReferenceId)
      .filter((id): id is string => !!id);
  }, [row.deduplication]);

  // Fetch CC Patient data using RTK Query
  const shouldFetch = expandedSection === 'deduplication' && ccPatientIds.length > 0;

  const {
    data: ccPatientsData,
    isLoading: isLoadingDedupe,
    error: dedupeError,
  } = useGetCCPatientsByIdsQuery(ccPatientIds, {
    skip: !shouldFetch,
  });

  // Merge CC Patient data with deduplication matches
  const enrichedDeduplicationData = useMemo(() => {
    if (!ccPatientsData || !row.deduplication) {
      return [];
    }

    return row.deduplication.map((dedupeMatch) => {
      const ccData = ccPatientsData[dedupeMatch.ccPatientReferenceId];
      return {
        ...dedupeMatch,
        ...(ccData || {}),
      };
    });
  }, [row.deduplication, ccPatientsData]);

  return (
    <Box sx={{ margin: 1.25 }}>
      {/* Patient and Caregiver Information Grid */}
      <PatientInfoGrid
        row={row}
        onSendToCC={onSendToCC}
        isSending={isSending}
        selectedDedupeRecord={selectedDedupeRecord}
        isProcessedTab={isProcessedTab}
        fieldConfig={expandedConfig?.personal}
        personalInfoHeaderText={expandedConfig?.personalInfoHeaderText}
        statusValue={statusValue}
        viewDetails={expandedConfig?.viewDetails}
        sendToCC={expandedConfig?.sendToCC}
      />

      {/* Caregiver Information Accordion */}
      <CaregiverSection
        caregiver={row.caregiver}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
        fieldConfig={expandedConfig?.caregiverInfo}
        headerText={expandedConfig?.caregiverInfoHeaderText}
      />

      {/* Tracking Parameters Accordion */}
      {/* <TrackingParametersSection
        trackingParams={{
          utm_source: row.utm_source,
          utm_medium: row.utm_medium,
          utm_campaign: row.utm_campaign,
          utm_term: row.utm_term,
          utm_content: row.utm_content,
          sID: row.sID,
          psecID: row.psecID,
          gclid: row.gclid,
          fbclid: row.fbclid,
          atid: row.atid,
        }}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
        fieldConfig={expandedConfig?.trackingParams}
        headerText={expandedConfig?.trackingParamsHeaderText}
      /> */}

      {/* Deduplication Additional Parameters - Only show if status is not "New-Record" */}
      {row.deduplication && row.deduplication.length > 0 && statusValue !== STATUS.NEW_RECORD && (
        <CustomAccordion
          title={expandedConfig?.dedupAddParamsHeaderText || 'Deduplication Additional Parameters'}
          expanded={expandedSection === 'deduplication'}
          onChange={() =>
            setExpandedSection(expandedSection === 'deduplication' ? null : 'deduplication')
          }
          detailsSx={{ p: '0!important' }}
        >
          {isLoadingDedupe ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 2 }}>
                Loading deduplication details...
              </Typography>
            </Box>
          ) : dedupeError ? (
            <Box sx={{ p: 2 }}>
              <Typography color="error" variant="body2">
                {typeof dedupeError === 'object' && 'data' in dedupeError
                  ? String(dedupeError.data)
                  : 'Failed to load deduplication data'}
              </Typography>
            </Box>
          ) : enrichedDeduplicationData.length > 0 ? (
            <DeduplicationTable
              deduplicationMatches={enrichedDeduplicationData}
              onOpenMergeModal={onOpenMergeModal}
              selectedRecordId={selectedDedupeRecord}
              onRecordSelect={setSelectedDedupeRecord}
              hideSelectColumn={hideDedupeSelectColumn}
              fieldConfig={expandedConfig?.dedupAddParams}
              parentData={{
                phoneNumber: row.phone, // to match with dedupe row phone
                deduplication: row.deduplication,
              }}
            />
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No deduplication matches found.
              </Typography>
            </Box>
          )}
        </CustomAccordion>
      )}

      {/* Comments Section */}
      {/* <CommentsSection
        comments={row.comments}
        expandedSection={expandedSection}
        setExpandedSection={setExpandedSection}
        newComment={newComment}
        setNewComment={setNewComment}
        headerText={expandedConfig?.commentsHeaderText}
      /> */}

      {/* Audit Changes Section - Only show if there are audit changes */}
      {row.auditChanges && row.auditChanges.length > 0 && (
        <AuditChangesSection
          auditChanges={row.auditChanges}
          expandedSection={expandedSection}
          setExpandedSection={setExpandedSection}
          fieldConfig={expandedConfig?.auditChanges}
        />
      )}
    </Box>
  );
};

export default ExpandedRowContent;
